import React, { useState, useEffect } from 'react';
import { useDaggerheartData } from '../../context/DataContext';
import { Star, Square, CheckSquare } from 'lucide-react';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';
import DomainCardPicker from './DomainCardPicker';
import DependencyWarningModal from './DependencyWarningModal';
import { detectDependencies, getLevelsToReset, generateDependencySummary } from '../../utils/levelDependencies';
import {
  getSubclassLevelUpBonuses,
  applySubclassStatBonuses,
  applySubclassThresholdBonuses,
  getBeastboundCompanionUpgrades,
  getSchoolOfKnowledgeBonusCards,
  getSchoolOfKnowledgeFeatureName
} from '../../utils/subclassLevelUpBonuses';
import { hasSubclass } from '../../utils/featureDetection';
import { getCompanion, updateCompanion } from '../../services/companionService';
import CompanionLevelUpManager from '../CompanionLevelUpManager';
import ConversionPlanBanner from './ConversionPlanBanner';

/**
 * LevelAdvancementEditor - Level up interface for levels 2+
 *
 * Inspired by official Daggerheart level-up sheet:
 * - Tier-based organization
 * - Visual tracking of marks and permanent choices
 * - Scannable layout showing all options
 * - Clear tier achievements
 * - Integrated companion leveling for Beastbound Rangers
 */
function LevelAdvancementEditor({ level, character, onUpdate, onLevelComplete }) {
  const allData = useDaggerheartData();
  const [selectedAdvancements, setSelectedAdvancements] = useState([]);
  const [selectedTraits, setSelectedTraits] = useState([]);
  const [selectedExperiences, setSelectedExperiences] = useState([]);
  const [selectedDomainCards, setSelectedDomainCards] = useState([]);

  // Mandatory domain card selection (Step 4)
  const [mandatoryDomainCard, setMandatoryDomainCard] = useState(null);
  const [swapOldCard, setSwapOldCard] = useState(null);

  // School of Knowledge bonus domain cards
  const [bonusDomainCards, setBonusDomainCards] = useState([]);

  // New experience naming (tier boundaries: L2/L5/L8)
  const [newExperienceName, setNewExperienceName] = useState('');

  // Two Experiences selection
  const [twoExperiencesChoice, setTwoExperiencesChoice] = useState([null, null]);

  // Double-submit protection
  const [isApplying, setIsApplying] = useState(false);

  // Dependency warning modal state
  const [showDependencyWarning, setShowDependencyWarning] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [affectedLevels, setAffectedLevels] = useState({});
  const [levelsToReset, setLevelsToReset] = useState([]);

  // Companion upgrade state (for Beastbound Rangers)
  const [companionUpgradeMode, setCompanionUpgradeMode] = useState(false);
  const [companion, setCompanion] = useState(null);
  const [companionUpgradesNeeded, setCompanionUpgradesNeeded] = useState(0);

  // Load previous choices if editing this level
  useEffect(() => {
    const levelHistory = character.levelHistory?.[level];
    if (levelHistory?.choices) {
      const choices = levelHistory.choices;

      // Restore advancement selections
      if (choices.advancements?.length > 0) {
        const restoredAdvancements = choices.advancements.map(id => ({
          id,
          slots: id === 'proficiency' || id === 'multiclass' ? 2 : 1
        }));
        setSelectedAdvancements(restoredAdvancements);
      }

      // Restore trait selections
      if (choices.selectedTraits?.length > 0) {
        setSelectedTraits(choices.selectedTraits);
      }

      // Restore domain card selections (advancement option)
      if (choices.selectedDomainCards?.length > 0) {
        setSelectedDomainCards(choices.selectedDomainCards);
      }

      // Restore mandatory domain card selection
      if (choices.mandatoryDomainCard) {
        setMandatoryDomainCard(choices.mandatoryDomainCard);
      }

      // Restore card swap selection
      if (choices.swapOldCard) {
        setSwapOldCard(choices.swapOldCard);
      }

      // Restore School of Knowledge bonus cards
      if (choices.bonusDomainCards?.length > 0) {
        setBonusDomainCards(choices.bonusDomainCards);
      }

      // Restore new experience name (tier boundaries)
      if (choices.newExperienceName) {
        setNewExperienceName(choices.newExperienceName);
      }

      // Restore two experiences choice
      if (choices.twoExperiencesChoice) {
        setTwoExperiencesChoice(choices.twoExperiencesChoice);
      }
    } else {
      // Clear all selections for new/unsaved levels
      setSelectedAdvancements([]);
      setSelectedTraits([]);
      setSelectedExperiences([]);
      setSelectedDomainCards([]);
      setMandatoryDomainCard(null);
      setSwapOldCard(null);
      setBonusDomainCards([]);
      setNewExperienceName('');
      setTwoExperiencesChoice([null, null]);
    }
  }, [level, character.levelHistory]);

  // Pre-fill from conversionPlan if no previous choices exist
  useEffect(() => {
    if (!character?.conversionPlan?.recommendations) return;
    const rec = character.conversionPlan.recommendations[String(level)];
    if (!rec) return;

    // Only pre-fill if the user hasn't already made choices for this level
    const existingChoices = character.levelHistory?.[level]?.choices;
    if (existingChoices?.advancements?.length > 0) return;

    // Pre-select advancement slots
    if (rec.advancementSlots?.length > 0 && selectedAdvancements.length === 0) {
      const prefilled = rec.advancementSlots.map((id) => ({
        id,
        slots: id === 'proficiency' || id === 'multiclass' ? 2 : 1,
      }));
      setSelectedAdvancements(prefilled);
    }

    // Pre-select trait bumps
    if (rec.traitBumps?.length > 0 && selectedTraits.length === 0) {
      setSelectedTraits(rec.traitBumps);
    }

    // Pre-select domain card
    if (rec.domainCard?.cardId && !mandatoryDomainCard) {
      setMandatoryDomainCard(rec.domainCard.cardId);
      if (rec.domainCard.action === 'swap' && rec.domainCard.swapOut) {
        setSwapOldCard(rec.domainCard.swapOut);
      }
    }
  }, [character?.conversionPlan, level]); // Intentionally excludes selectedAdvancements, selectedTraits, mandatoryDomainCard - only run on plan/level change

  if (!allData) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">Failed to load game data. Please refresh.</p>
      </div>
    );
  }

  // Determine tier based on level
  const tier = level <= 1 ? 1 : level <= 4 ? 2 : level <= 7 ? 3 : 4;
  const isTierBoundary = level === 2 || level === 5 || level === 8;

  // Get trait marks for this tier
  const traitMarks = character.traitMarks || {};
  const markedTraitsThisTier = Object.entries(traitMarks)
    .filter(([_, data]) => data.marked && data.tier === tier)
    .map(([trait]) => trait);

  // Count advancement slots (always 2 per level)
  const maxSlots = 2;
  const usedSlots = selectedAdvancements.reduce((sum, adv) => {
    return sum + (adv.id === 'proficiency' || adv.id === 'multiclass' ? 2 : 1);
  }, 0);
  const availableSlots = maxSlots - usedSlots;

  // Check if two_traits advancement is selected
  const hasTraitAdvancement = selectedAdvancements.some(a => a.id === 'two_traits');

  // Check if domain_card advancement is selected
  const hasDomainCardAdvancement = selectedAdvancements.some(a => a.id === 'domain_card');

  // School of Knowledge bonus cards
  const schoolOfKnowledgeBonusCount = getSchoolOfKnowledgeBonusCards(character, level);
  const schoolOfKnowledgeFeatureName = getSchoolOfKnowledgeFeatureName(level);

  // Determine multiclass domain (if character has multiclassed)
  // Multiclass would have been chosen at a previous level, stored in levelHistory
  const multiclassDomain = character.multiclassClass
    ? allData.classes?.[character.multiclassClass]?.domains?.[0]
    : null;

  /**
   * Apply tier boundary effects (automatic)
   *
   * IMPORTANT: Guard prevents infinite loop by only clearing marks if they exist.
   * Without the guard, this would call onUpdate → re-render → useEffect fires again.
   */
  useEffect(() => {
    const hasMarks = Object.keys(character.traitMarks || {}).length > 0;

    if (isTierBoundary && tier >= 3 && hasMarks) {
      // Clear all trait marks at tier boundaries (L5, L8)
      onUpdate({
        traitMarks: {}
      });
    }
  }, [isTierBoundary, tier, character.traitMarks, onUpdate]);

  /**
   * Confirm and apply advancements
   */
  function handleConfirmAdvancements() {
    // Validation
    if (usedSlots !== maxSlots) {
      toast.error(`You must select ${maxSlots} advancement slots`);
      return;
    }

    if (hasTraitAdvancement && selectedTraits.length !== 2) {
      toast.error('Select 2 traits to upgrade');
      return;
    }

    if (hasDomainCardAdvancement && selectedDomainCards.length !== 1) {
      toast.error('Select 1 domain card');
      return;
    }

    // Mandatory domain card validation (Step 4)
    if (!mandatoryDomainCard && !swapOldCard) {
      toast.error('Step 4: You must select a new domain card or swap an existing one');
      return;
    }

    if (swapOldCard && !mandatoryDomainCard) {
      toast.error('Step 4: Select a replacement card for the swap');
      return;
    }

    // School of Knowledge bonus cards validation
    if (schoolOfKnowledgeBonusCount > 0 && bonusDomainCards.length < schoolOfKnowledgeBonusCount) {
      toast.error(`School of Knowledge: Select ${schoolOfKnowledgeBonusCount} bonus domain card(s)`);
      return;
    }

    // New experience naming validation (tier boundaries)
    if (isTierBoundary && !newExperienceName.trim()) {
      toast.error('Tier Achievement: Please name your new experience');
      return;
    }

    // Two Experiences validation
    const hasTwoExperiences = selectedAdvancements.some(a => a.id === 'two_experiences');
    if (hasTwoExperiences && (twoExperiencesChoice[0] === null || twoExperiencesChoice[1] === null)) {
      toast.error('Two Experiences: Please select both experiences to improve');
      return;
    }

    // Check if editing a previously completed level
    const isEditing = character.levelHistory?.[level] !== undefined;

    if (isEditing) {
      // Build new choices object
      const newChoices = {
        advancements: selectedAdvancements.map(a => a.id),
        selectedTraits: hasTraitAdvancement ? selectedTraits : undefined,
        selectedDomainCards: hasDomainCardAdvancement ? selectedDomainCards : undefined
      };

      // Get old choices
      const oldChoices = character.levelHistory[level].choices;

      // Detect dependencies
      const dependencies = detectDependencies(level, oldChoices, newChoices, character);
      const resetLevels = getLevelsToReset(dependencies);

      if (Object.keys(dependencies).length > 0) {
        // Show warning modal
        setAffectedLevels(dependencies);
        setLevelsToReset(resetLevels);
        setPendingUpdate({ newChoices });
        setShowDependencyWarning(true);
        return;
      }
    }

    // No dependencies or new level - proceed with save
    applyAdvancements();
  }

  /**
   * Apply advancements (called directly or after dependency confirmation)
   */
  async function applyAdvancements(resetLevels = []) {
    if (isApplying) return;
    setIsApplying(true);
    // Build updated character state
    const updates = {};

    // Initialize permanentSlots if not present
    if (!character.permanentSlots) {
      updates.permanentSlots = {};
    } else {
      updates.permanentSlots = { ...character.permanentSlots };
    }

    // Initialize loadout - if editing, remove cards added in previous attempt at this level
    const previousChoices = character.levelHistory?.[level]?.choices;
    if (previousChoices) {
      // Remove cards that were added in the previous version of this level
      let loadout = [...(character.loadout || [])];

      // Remove previous mandatory card
      if (previousChoices.mandatoryDomainCard) {
        loadout = loadout.filter(id => id !== previousChoices.mandatoryDomainCard);
      }

      // Remove previous optional cards
      if (previousChoices.selectedDomainCards) {
        previousChoices.selectedDomainCards.forEach(cardId => {
          loadout = loadout.filter(id => id !== cardId);
        });
      }

      // Remove previous bonus cards
      if (previousChoices.bonusDomainCards) {
        previousChoices.bonusDomainCards.forEach(cardId => {
          loadout = loadout.filter(id => id !== cardId);
        });
      }

      updates.loadout = loadout;

      // Remove previously-added tier experience to prevent duplicates on re-edit
      if (previousChoices.newExperienceName) {
        let experiences = [...(character.experiences || [])];
        // Find and remove the last occurrence (the one added by this level)
        const prevName = previousChoices.newExperienceName;
        for (let i = experiences.length - 1; i >= 0; i--) {
          const exp = experiences[i];
          const name = typeof exp === 'string' ? exp : exp?.name;
          if (name === prevName) {
            experiences.splice(i, 1);
            break;
          }
        }
        updates.experiences = experiences;
      }

      // Revert previous two_experiences upgrades
      if (previousChoices.twoExperiencesChoice) {
        let experiences = updates.experiences || [...(character.experiences || [])];
        const [idx0, idx1] = previousChoices.twoExperiencesChoice;
        experiences = experiences.map((exp, idx) => {
          if (idx === idx0 || idx === idx1) {
            if (typeof exp === 'object' && exp?.value > 2) {
              return { ...exp, value: exp.value - 1 };
            }
          }
          return exp;
        });
        updates.experiences = experiences;
      }
    }

    // Apply each advancement
    selectedAdvancements.forEach(advancement => {
      // Track tier-wide usage for this advancement
      const advId = advancement.id;
      if (!updates.permanentSlots[advId]) {
        updates.permanentSlots[advId] = {};
      }
      const currentTierUsage = updates.permanentSlots[advId][tier] || 0;
      updates.permanentSlots[advId][tier] = currentTierUsage + 1;
      switch (advancement.id) {
        case 'two_traits':
          // Update trait values
          const newTraits = { ...character.traits };
          selectedTraits.forEach(trait => {
            newTraits[trait] = (newTraits[trait] || 0) + 1;
          });
          updates.traits = newTraits;

          // Mark the selected traits
          const newMarks = { ...character.traitMarks };
          selectedTraits.forEach(trait => {
            newMarks[trait] = { marked: true, tier, level };
          });
          updates.traitMarks = newMarks;
          break;

        case 'hp':
          updates.maxHp = (character.maxHp || 0) + 1;
          // Tier-wide tracking handled above
          break;

        case 'stress':
          updates.maxStress = (character.maxStress || 0) + 1;
          // Tier-wide tracking handled above
          break;

        case 'evasion':
          updates.evasion = (character.evasion || 0) + 1;
          break;

        case 'proficiency':
          updates.proficiency = (character.proficiency || 1) + 1;
          break;

        case 'two_experiences':
          // Apply +1 to two selected experiences (use updates.experiences if already cleaned up from re-edit)
          const currentExperiences = updates.experiences || character.experiences || [];
          const updatedExperiences = currentExperiences.map((exp, idx) => {
            if (idx === twoExperiencesChoice[0] || idx === twoExperiencesChoice[1]) {
              // Handle both string and object formats
              if (typeof exp === 'string') {
                return { name: exp, value: 3 }; // Default 2 + 1
              } else {
                return {
                  ...exp,
                  value: (exp.value || 2) + 1
                };
              }
            }
            return exp;
          });
          updates.experiences = updatedExperiences;
          break;

        case 'domain_card':
          // Add selected domain card to loadout (from optional advancement)
          const currentLoadout = updates.loadout || character.loadout || [];
          updates.loadout = [...currentLoadout, ...selectedDomainCards];
          break;

        default:
          break;
      }
    });

    // ==========================================
    // MANDATORY DOMAIN CARD (STEP 4)
    // ==========================================
    if (swapOldCard && mandatoryDomainCard) {
      // Swap: Remove old card, add new card
      const currentLoadout = updates.loadout || character.loadout || [];
      if (!updates.loadout) {
        updates.loadout = [...currentLoadout];
      }
      updates.loadout = updates.loadout.filter(id => id !== swapOldCard);
      updates.loadout.push(mandatoryDomainCard);
    } else if (mandatoryDomainCard && !swapOldCard) {
      // Add new card
      const currentLoadout = updates.loadout || character.loadout || [];
      if (!updates.loadout) {
        updates.loadout = [...currentLoadout];
      }
      updates.loadout.push(mandatoryDomainCard);
    }

    // ==========================================
    // SCHOOL OF KNOWLEDGE BONUS CARDS
    // ==========================================
    if (bonusDomainCards.length > 0) {
      // Add bonus cards to loadout
      const currentLoadout = updates.loadout || character.loadout || [];
      if (!updates.loadout) {
        updates.loadout = [...currentLoadout];
      }
      updates.loadout.push(...bonusDomainCards);
    }

    // Apply tier achievements if tier boundary
    if (isTierBoundary) {
      // Tier achievement: +1 Proficiency
      updates.proficiency = (character.proficiency || 1) + 1;

      // Tier achievement: New experience at +2
      // Use updates.experiences if already modified (e.g., by two_experiences), otherwise character.experiences
      const currentExperiences = updates.experiences || character.experiences || [];
      const newExperience = {
        name: newExperienceName.trim(),
        value: 2
      };
      updates.experiences = [...currentExperiences, newExperience];
    }

    // ==========================================
    // AUTO-APPLY DAMAGE THRESHOLDS & SUBCLASS BONUSES
    // ==========================================

    // Get subclass bonuses for this level-up
    const subclassBonuses = getSubclassLevelUpBonuses(character, level);

    // Auto-increment damage thresholds (+1 per level + subclass bonuses)
    const newThresholds = applySubclassThresholdBonuses(character, subclassBonuses, 1);
    updates.damageThresholds = newThresholds;

    // Apply subclass stat bonuses (HP, Stress, Evasion)
    const characterWithStats = applySubclassStatBonuses({ ...character, ...updates }, subclassBonuses);
    if (subclassBonuses.stats.maxHitPoints) {
      updates.maxHitPoints = characterWithStats.maxHitPoints;
    }
    if (subclassBonuses.stats.maxStress) {
      updates.maxStress = characterWithStats.maxStress;
    }
    if (subclassBonuses.stats.evasion) {
      updates.evasion = characterWithStats.evasion;
    }

    // Log subclass bonuses if any were applied (for debugging/transparency)
    if (Object.keys(subclassBonuses.stats).length > 0 || Object.keys(subclassBonuses.thresholds).length > 0) {
      console.log(`[Level ${level}] Applied subclass bonuses:`, subclassBonuses);
    }

    // Save level history for this level
    const levelHistory = character.levelHistory || {};

    // Build choices object without undefined values
    const choices = {
      advancements: selectedAdvancements.map(a => a.id),
      permanentSlots: updates.permanentSlots,
      tier,
      isTierBoundary
    };

    // Only add optional fields if they have values
    if (hasTraitAdvancement && selectedTraits.length > 0) {
      choices.selectedTraits = selectedTraits;
      choices.traitMarks = updates.traitMarks;
    }
    if (hasDomainCardAdvancement && selectedDomainCards.length > 0) {
      choices.selectedDomainCards = selectedDomainCards;
    }
    if (mandatoryDomainCard) {
      choices.mandatoryDomainCard = mandatoryDomainCard;
      if (swapOldCard) {
        choices.swapOldCard = swapOldCard;
      }
    }
    if (bonusDomainCards.length > 0) {
      choices.bonusDomainCards = bonusDomainCards;
    }
    if (isTierBoundary && newExperienceName.trim()) {
      choices.newExperienceName = newExperienceName.trim();
    }
    if (twoExperiencesChoice[0] !== null && twoExperiencesChoice[1] !== null) {
      choices.twoExperiencesChoice = twoExperiencesChoice;
    }

    levelHistory[level] = {
      choices,
      timestamp: Date.now(),
      appliedUpdates: { ...updates } // Store what was actually changed
    };

    // Reset affected levels if specified
    if (resetLevels.length > 0) {
      resetLevels.forEach(resetLevel => {
        delete levelHistory[resetLevel];
      });

      toast.success(`Level ${level} saved. Levels ${resetLevels.join(', ')} have been reset.`);
    }

    updates.levelHistory = levelHistory;

    // Mark this level as complete
    updates.level = Math.max(character.level || 1, level);

    // Deep clean: Remove all undefined values from updates object (Firestore doesn't accept undefined)
    const deepClean = (obj) => {
      if (obj === null || typeof obj !== 'object') {
        return obj;
      }

      if (Array.isArray(obj)) {
        return obj.map(item => deepClean(item));
      }

      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = deepClean(value);
        }
      }
      return cleaned;
    };

    const cleanUpdates = deepClean(updates);

    // Verify no undefined values remain
    const hasUndefined = JSON.stringify(cleanUpdates).includes('undefined');
    if (hasUndefined) {
      console.error('⚠️ CRITICAL: Undefined values still present after cleaning!');
      console.error('Clean updates:', JSON.stringify(cleanUpdates, null, 2));
      toast.error('Internal error: Invalid data detected. Check console.');
      setIsApplying(false);
      return;
    }

    // Save character updates FIRST
    onUpdate(cleanUpdates);

    if (resetLevels.length === 0) {
      toast.success(`Level ${level} advancements applied!`);
    }

    // Notify when conversion walkthrough is complete
    if (character?.conversionPlan?.targetLevel && level >= character.conversionPlan.targetLevel) {
      toast.success('Level-up walkthrough complete! Your character is ready.');
    }

    // ==========================================
    // BEASTBOUND COMPANION INTEGRATION
    // ==========================================
    if (hasSubclass(character, 'beastbound')) {
      try {
        const companionData = await getCompanion(character.id);
        if (companionData) {
          const companionUpgrades = getBeastboundCompanionUpgrades(level);

          if (companionUpgrades > 0) {
            // Update companion level and pending upgrades
            await updateCompanion(character.id, companionData.id, {
              level: level,
              pendingUpgrades: (companionData.pendingUpgrades || 0) + companionUpgrades
            });

            // Reload companion data and switch to companion upgrade mode
            const updatedCompanion = await getCompanion(character.id);
            setCompanion(updatedCompanion);
            setCompanionUpgradesNeeded(companionUpgrades);
            setCompanionUpgradeMode(true);

            // Show informational toast
            const upgradeReason = level === 5 ? ' (+1 Expert Training bonus!)' :
                                 level === 9 ? ' (+2 Advanced Training bonuses!)' : '';
            toast.success(
              `${companionData.name} also leveled up! Choose ${companionUpgrades} upgrade${companionUpgrades > 1 ? 's' : ''}${upgradeReason}`,
              { duration: 4000 }
            );
            return; // Exit early - will finish after companion upgrades
          }
        }
      } catch (error) {
        console.error('Failed to level up companion:', error);
        // Don't block character level-up if companion fails
        toast.error('Warning: Failed to level up companion. Please check CompanionSheet.');
      }
    }

    // Reset selections
    setSelectedAdvancements([]);
    setSelectedTraits([]);
    setSelectedDomainCards([]);
    setMandatoryDomainCard(null);
    setSwapOldCard(null);
    setBonusDomainCards([]);
    setNewExperienceName('');
    setTwoExperiencesChoice([null, null]);

    // Notify parent of level completion (for navigation logic)
    // Only trigger navigation for NEW level-ups (not editing existing levels)
    if (onLevelComplete && level === character.level + 1) {
      onLevelComplete(level);
    }

    setIsApplying(false);
  }

  /**
   * Handle dependency warning confirmation
   */
  function handleDependencyConfirm() {
    setShowDependencyWarning(false);
    applyAdvancements(levelsToReset);
    setPendingUpdate(null);
    setAffectedLevels({});
    setLevelsToReset([]);
  }

  /**
   * Handle dependency warning cancellation
   */
  function handleDependencyCancel() {
    setShowDependencyWarning(false);
    setPendingUpdate(null);
    setAffectedLevels({});
    setLevelsToReset([]);
    toast('Changes discarded');
  }

  /**
   * Handle companion upgrade completion
   */
  function handleCompanionUpgradeComplete() {
    // Show success message
    toast.success(`Level ${level} complete! ${companion?.name || 'Companion'} is ready for adventure!`);

    // Always notify parent to close the level-up interface
    // (Character level was already updated during applyAdvancements)
    if (onLevelComplete) {
      onLevelComplete(level);
    }

    // Reset all state after parent closes the interface
    setCompanionUpgradeMode(false);
    setCompanion(null);
    setCompanionUpgradesNeeded(0);
    setSelectedAdvancements([]);
    setSelectedTraits([]);
    setSelectedDomainCards([]);
    setMandatoryDomainCard(null);
    setSwapOldCard(null);
    setBonusDomainCards([]);
    setNewExperienceName('');
    setTwoExperiencesChoice([null, null]);
  }

  // Show companion upgrade UI if in companion upgrade mode
  if (companionUpgradeMode && companion) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header explaining companion upgrade step */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-amber-600 rounded-lg shadow-2xl p-6">
          <h2 className="text-heading-lg text-amber-400 font-serif mb-2">
            Companion Upgrade - {companion.name}
          </h2>
          <p className="text-body text-gray-300">
            Your character advancement is complete! Now choose {companionUpgradesNeeded} upgrade{companionUpgradesNeeded > 1 ? 's' : ''} for your companion.
          </p>
        </div>

        {/* Companion Level Up Manager */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-amber-600 rounded-lg shadow-2xl p-6">
          <CompanionLevelUpManager
            companion={companion}
            rangerId={character.id}
            onComplete={handleCompanionUpgradeComplete}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <ConversionPlanBanner character={character} currentLevel={level} />
      {/* Level Header - Outside cards like Level 1 */}
      <div className="mb-6">
        <h2 className="text-heading-lg text-red-400 font-serif mb-2">
          Level {level} - Tier {tier}
        </h2>
        <p className="text-body text-gray-300">
          Select advancement options and domain cards to level up your character
        </p>
      </div>

      {/* Tier Achievement (if tier boundary) - Separate card */}
      {isTierBoundary && (
        <div className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg shadow-2xl p-6">
          <TierAchievement
            level={level}
            tier={tier}
            newExperienceName={newExperienceName}
            onExperienceNameChange={setNewExperienceName}
          />
        </div>
      )}

      {/* Advancement Options */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-heading-md text-red-400 font-serif">
            Advancement Options
          </h3>
          <div className="text-sm text-gray-300">
            Slots: <span className="font-bold text-white">{availableSlots}/{maxSlots}</span>
          </div>
        </div>
        <p className="text-small text-gray-300 mb-4">
          Choose {maxSlots} options from the list below{tier >= 3 && ' or any from the previous tier'} and mark them.
        </p>

        <div className="space-y-3">
          {/* Two Traits Advancement */}
          <AdvancementOption
            id="two_traits"
            title="Gain +1 bonus to two unmarked character traits and mark them"
            slots={1}
            selected={selectedAdvancements.some(a => a.id === 'two_traits')}
            onSelect={() => handleAdvancementToggle('two_traits', 1)}
            disabled={availableSlots < 1}
            hasThreeBoxes
            character={character}
            tier={tier}
          >
            {selectedAdvancements.some(a => a.id === 'two_traits') && (
              <TraitSelector
                character={character}
                markedTraits={markedTraitsThisTier}
                selectedTraits={selectedTraits}
                onTraitToggle={handleTraitToggle}
                tier={tier}
              />
            )}
          </AdvancementOption>

          {/* HP Slot */}
          <AdvancementOption
            id="hp"
            title="Permanently gain one Hit Point slot"
            slots={1}
            selected={selectedAdvancements.some(a => a.id === 'hp')}
            onSelect={() => handleAdvancementToggle('hp', 1)}
            disabled={availableSlots < 1}
            hasTwoBoxes
            character={character}
            tier={tier}
          />

          {/* Stress Slot */}
          <AdvancementOption
            id="stress"
            title="Permanently gain one Stress slot"
            slots={1}
            selected={selectedAdvancements.some(a => a.id === 'stress')}
            onSelect={() => handleAdvancementToggle('stress', 1)}
            disabled={availableSlots < 1}
            hasTwoBoxes
            character={character}
            tier={tier}
          />

          {/* Two Experiences */}
          <AdvancementOption
            id="two_experiences"
            title="Permanently gain a +1 bonus to two Experiences"
            slots={1}
            selected={selectedAdvancements.some(a => a.id === 'two_experiences')}
            onSelect={() => handleAdvancementToggle('two_experiences', 1)}
            disabled={availableSlots < 1}
            character={character}
            tier={tier}
          >
            {selectedAdvancements.some(a => a.id === 'two_experiences') && (
              <div className="pl-8 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                <p className="text-small text-gray-400">
                  Choose which two experiences to improve by +1
                </p>

                {/* First Experience */}
                <div>
                  <label className="block text-small font-medium text-gray-300 mb-1">
                    First Experience
                  </label>
                  <select
                    value={twoExperiencesChoice[0] !== null ? twoExperiencesChoice[0] : ''}
                    onChange={(e) => {
                      const newChoice = [...twoExperiencesChoice];
                      newChoice[0] = e.target.value !== '' ? parseInt(e.target.value) : null;
                      setTwoExperiencesChoice(newChoice);
                    }}
                    className="w-full p-2 rounded-md bg-surface-3 border border-border-default text-gray-200"
                  >
                    <option value="">Select first experience...</option>
                    {(character.experiences || []).map((exp, idx) => {
                      const expName = typeof exp === 'string' ? exp : (exp.name || 'Unnamed Experience');
                      const expValue = typeof exp === 'string' ? 2 : (exp.value || 2);
                      return (
                        <option key={idx} value={idx}>
                          {expName} (+{expValue} → +{expValue + 1})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Second Experience */}
                <div>
                  <label className="block text-small font-medium text-gray-300 mb-1">
                    Second Experience
                  </label>
                  <select
                    value={twoExperiencesChoice[1] !== null ? twoExperiencesChoice[1] : ''}
                    onChange={(e) => {
                      const newChoice = [...twoExperiencesChoice];
                      newChoice[1] = e.target.value !== '' ? parseInt(e.target.value) : null;
                      setTwoExperiencesChoice(newChoice);
                    }}
                    className="w-full p-2 rounded-md bg-surface-3 border border-border-default text-gray-200"
                  >
                    <option value="">Select second experience...</option>
                    {(character.experiences || []).map((exp, idx) => {
                      const expName = typeof exp === 'string' ? exp : (exp.name || 'Unnamed Experience');
                      const expValue = typeof exp === 'string' ? 2 : (exp.value || 2);
                      return (
                        <option key={idx} value={idx}>
                          {expName} (+{expValue} → +{expValue + 1})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Validation */}
                {(twoExperiencesChoice[0] === null || twoExperiencesChoice[1] === null) && (
                  <p className="text-tiny text-yellow-400 font-medium">
                    ⚠ Select both experiences to complete this advancement
                  </p>
                )}
              </div>
            )}
          </AdvancementOption>

          {/* Domain Card */}
          <AdvancementOption
            id="domain_card"
            title={`Choose an additional domain card of your level or lower from a domain you have access to (up to level ${level})`}
            slots={1}
            selected={selectedAdvancements.some(a => a.id === 'domain_card')}
            onSelect={() => handleAdvancementToggle('domain_card', 1)}
            disabled={availableSlots < 1}
            character={character}
            tier={tier}
          >
            {selectedAdvancements.some(a => a.id === 'domain_card') && (
              <div onClick={(e) => e.stopPropagation()}>
                <DomainCardPicker
                  character={character}
                  allData={allData}
                  maxLevel={level}
                  maxCards={1}
                  selectedCards={selectedDomainCards}
                  onToggleCard={handleDomainCardToggle}
                  multiclassDomain={multiclassDomain}
                />
              </div>
            )}
          </AdvancementOption>

          {/* Evasion */}
          <AdvancementOption
            id="evasion"
            title="Permanently gain a +1 bonus to your Evasion"
            slots={1}
            selected={selectedAdvancements.some(a => a.id === 'evasion')}
            onSelect={() => handleAdvancementToggle('evasion', 1)}
            disabled={availableSlots < 1}
            character={character}
            tier={tier}
          />

          {/* Tier 3+ Options */}
          {tier >= 3 && (
            <>
              {/* Subclass Upgrade */}
              <AdvancementOption
                id="subclass_upgrade"
                title="Take an upgraded subclass card. Then cross out the multiclass option for this tier."
                slots={1}
                selected={selectedAdvancements.some(a => a.id === 'subclass_upgrade')}
                onSelect={() => handleAdvancementToggle('subclass_upgrade', 1)}
                disabled={availableSlots < 1}
                emphasized
                character={character}
                tier={tier}
              />

              {/* Proficiency */}
              <AdvancementOption
                id="proficiency"
                title="Increase your Proficiency by +1"
                slots={2}
                selected={selectedAdvancements.some(a => a.id === 'proficiency')}
                onSelect={() => handleAdvancementToggle('proficiency', 2)}
                disabled={availableSlots < 2}
                doubleSlot
                character={character}
                tier={tier}
              />

              {/* Multiclass */}
              <AdvancementOption
                id="multiclass"
                title="Multiclass: Choose an additional class for your character, then cross out an unused 'Take an upgraded subclass card' and the other multiclass option on this sheet."
                slots={2}
                selected={selectedAdvancements.some(a => a.id === 'multiclass')}
                onSelect={() => handleAdvancementToggle('multiclass', 2)}
                disabled={availableSlots < 2}
                doubleSlot
                character={character}
                tier={tier}
              />
            </>
          )}
        </div>
      </div>

      {/* Step 4: Mandatory Domain Card Selection */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg">
            4
          </div>
          <div>
            <h3 className="text-heading-md text-red-400 font-serif">Select Domain Card (Required)</h3>
            <p className="text-small text-gray-400">
              Choose 1 new domain card (level {level} or lower) OR swap an existing card
            </p>
          </div>
        </div>

        {/* New Domain Card Option */}
        <div className="mb-4">
          <h4 className="text-small font-semibold text-gray-200 mb-2">Add New Domain Card</h4>
          <div onClick={(e) => e.stopPropagation()}>
            <DomainCardPicker
              character={character}
              allData={allData}
              maxLevel={level}
              maxCards={1}
              selectedCards={mandatoryDomainCard ? [mandatoryDomainCard] : []}
              onToggleCard={(cardId) => {
                setMandatoryDomainCard(cardId === mandatoryDomainCard ? null : cardId);
                setSwapOldCard(null); // Clear swap if selecting new card
              }}
              multiclassDomain={multiclassDomain}
            />
          </div>
        </div>

        {/* OR Swap Existing Card */}
        <div className="border-t border-border-default pt-4">
          <h4 className="text-small font-semibold text-gray-200 mb-2">Or Swap an Existing Card</h4>
          {character.loadout && character.loadout.length > 0 ? (
            <div className="space-y-2">
              <select
                value={swapOldCard || ''}
                onChange={(e) => {
                  setSwapOldCard(e.target.value || null);
                  if (e.target.value) {
                    setMandatoryDomainCard(null); // Clear new card if swapping
                  }
                }}
                className="w-full p-2 rounded-md bg-surface-3 border border-border-default text-gray-200"
              >
                <option value="">Select card to swap out...</option>
                {character.loadout.map(cardId => {
                  const card = allData.domainCards?.[cardId];
                  return card ? (
                    <option key={cardId} value={cardId}>
                      {card.name} (Level {card.level})
                    </option>
                  ) : null;
                })}
              </select>

              {swapOldCard && (
                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                  <p className="text-tiny text-gray-400 mb-2">Select replacement card:</p>
                  <DomainCardPicker
                    character={character}
                    allData={allData}
                    maxLevel={level}
                    maxCards={1}
                    selectedCards={mandatoryDomainCard ? [mandatoryDomainCard] : []}
                    onToggleCard={(cardId) => {
                      setMandatoryDomainCard(cardId === mandatoryDomainCard ? null : cardId);
                    }}
                    multiclassDomain={multiclassDomain}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="text-small text-gray-500 italic">No domain cards to swap</p>
          )}
        </div>

        {/* School of Knowledge Bonus Cards */}
        {schoolOfKnowledgeBonusCount > 0 && (
          <div className="border-t border-border-default pt-4">
            <div className="p-4 bg-purple-900/20 border border-purple-700 rounded-lg mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-purple-400" />
                <h4 className="text-small font-semibold text-purple-300">
                  School of Knowledge Bonus
                </h4>
              </div>
              <p className="text-small text-purple-400">
                <strong>{schoolOfKnowledgeFeatureName}</strong>: Select {schoolOfKnowledgeBonusCount} additional
                domain {schoolOfKnowledgeBonusCount === 1 ? 'card' : 'cards'}
              </p>
            </div>

            <div onClick={(e) => e.stopPropagation()}>
              <DomainCardPicker
                character={character}
                allData={allData}
                maxLevel={level}
                maxCards={schoolOfKnowledgeBonusCount}
                selectedCards={bonusDomainCards}
                onToggleCard={(cardId) => {
                  setBonusDomainCards(prev => {
                    if (prev.includes(cardId)) {
                      return prev.filter(id => id !== cardId);
                    } else {
                      if (prev.length < schoolOfKnowledgeBonusCount) {
                        return [...prev, cardId];
                      }
                      return prev;
                    }
                  });
                }}
                multiclassDomain={multiclassDomain}
              />
            </div>

            {bonusDomainCards.length < schoolOfKnowledgeBonusCount && (
              <div className="mt-3 p-3 bg-yellow-900/20 border border-yellow-700 rounded-lg">
                <p className="text-tiny text-yellow-400 font-medium">
                  ⚠ Select {schoolOfKnowledgeBonusCount - bonusDomainCards.length} more
                  {schoolOfKnowledgeBonusCount - bonusDomainCards.length === 1 ? ' card' : ' cards'}
                </p>
              </div>
            )}
            {bonusDomainCards.length === schoolOfKnowledgeBonusCount && (
              <div className="mt-3 p-3 bg-green-900/20 border border-green-700 rounded-lg">
                <p className="text-tiny text-green-400 font-medium">
                  ✓ Bonus domain cards selected
                </p>
              </div>
            )}
          </div>
        )}

        {/* Selection Status */}
        {!mandatoryDomainCard && !swapOldCard && (
          <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-700 rounded-lg">
            <p className="text-tiny text-yellow-400 font-medium">
              ⚠ You must select a new domain card or swap an existing one to proceed
            </p>
          </div>
        )}
        {mandatoryDomainCard && !swapOldCard && (
          <div className="mt-4 p-3 bg-green-900/20 border border-green-700 rounded-lg">
            <p className="text-tiny text-green-400 font-medium">
              ✓ New domain card selected
            </p>
          </div>
        )}
        {swapOldCard && mandatoryDomainCard && (
          <div className="mt-4 p-3 bg-green-900/20 border border-green-700 rounded-lg">
            <p className="text-tiny text-green-400 font-medium">
              ✓ Card swap configured
            </p>
          </div>
        )}
      </div>

      {/* Confirm Button */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => {
            setSelectedAdvancements([]);
            setSelectedTraits([]);
            setSelectedDomainCards([]);
            setMandatoryDomainCard(null);
            setSwapOldCard(null);
            setBonusDomainCards([]);
            setNewExperienceName('');
            setTwoExperiencesChoice([null, null]);
          }}
          className="px-8 py-3 bg-surface-2 text-gray-300 rounded-lg hover:bg-surface-3 transition-colors font-medium"
        >
          Clear Selections
        </button>
        <button
          onClick={handleConfirmAdvancements}
          disabled={usedSlots !== maxSlots}
          className={cn(
            "px-8 py-3 rounded-lg font-bold transition-colors",
            usedSlots === maxSlots
              ? "bg-primary text-white hover:bg-primary/90"
              : "bg-surface-3 text-gray-500 cursor-not-allowed"
          )}
        >
          Confirm Level Up
        </button>
      </div>

      {/* Dependency Warning Modal */}
      <DependencyWarningModal
        isOpen={showDependencyWarning}
        onClose={handleDependencyCancel}
        onConfirm={handleDependencyConfirm}
        editingLevel={level}
        affectedLevels={affectedLevels}
        levelsToReset={levelsToReset}
      />
    </div>
  );

  function handleAdvancementToggle(id, slotCost) {
    setSelectedAdvancements(prev => {
      const exists = prev.find(a => a.id === id);

      if (exists) {
        // Remove selection
        if (id === 'two_traits') {
          setSelectedTraits([]);
        }
        if (id === 'domain_card') {
          setSelectedDomainCards([]);
        }
        if (id === 'two_experiences') {
          setTwoExperiencesChoice([null, null]);
        }
        return prev.filter(a => a.id !== id);
      } else {
        // Add selection (if slots available)
        if (availableSlots >= slotCost) {
          return [...prev, { id, slots: slotCost }];
        }
        return prev;
      }
    });
  }

  function handleTraitToggle(trait) {
    setSelectedTraits(prev => {
      if (prev.includes(trait)) {
        return prev.filter(t => t !== trait);
      } else {
        if (prev.length < 2) {
          return [...prev, trait];
        }
        return prev;
      }
    });
  }

  function handleDomainCardToggle(cardId) {
    setSelectedDomainCards(prev => {
      if (prev.includes(cardId)) {
        return prev.filter(id => id !== cardId);
      } else {
        // Only allow 1 card for level-up
        return [cardId];
      }
    });
  }
}

/**
 * TierAchievement - Displays automatic tier achievements
 */
function TierAchievement({ level, tier, newExperienceName, onExperienceNameChange }) {
  return (
    <div>
      {/* Level Achievements */}
      <div className="mb-4">
        <h3 className="text-heading-md text-red-400 font-serif mb-3">Level Achievements:</h3>
        <ul className="space-y-1 text-small text-gray-300 ml-4">
          <li>• Gain a +1 bonus to your Proficiency</li>
          {tier >= 3 && <li>• Clear all marks on character traits</li>}
          <li>• Gain a new Experience at +2</li>
        </ul>
      </div>

      {/* New Experience Naming */}
      <div className="space-y-2">
        <label className="block text-small font-medium text-gray-300">
          Name your new experience
        </label>
        <input
          type="text"
          placeholder="Enter your new experience name (e.g., 'Survived the Shadow Vale')"
          value={newExperienceName}
          onChange={(e) => onExperienceNameChange(e.target.value)}
          className="w-full p-3 rounded-md bg-surface-3 border border-border-default
                     text-gray-200 placeholder-gray-500 focus:border-primary focus:outline-none"
          required
        />
        {!newExperienceName.trim() && (
          <p className="text-tiny text-yellow-400 font-medium">
            ⚠ Please name your new experience before continuing
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * AdvancementOption - Individual advancement choice
 */
function AdvancementOption({
  id,
  title,
  slots,
  selected,
  onSelect,
  disabled,
  hasThreeBoxes,
  hasTwoBoxes,
  doubleSlot,
  emphasized,
  children,
  character,
  tier
}) {
  // Calculate tier-wide usage from permanentSlots
  const maxSlots = hasThreeBoxes ? 3 : hasTwoBoxes ? 2 : 1;
  const usedSlots = character?.permanentSlots?.[id]?.[tier] || 0;
  const isMaxedOut = usedSlots >= maxSlots;

  // Allow clicking if selected (for deselection) OR if not disabled
  const canClick = selected || (!disabled && !isMaxedOut);

  return (
    <div
      className={cn(
        "border-2 rounded-lg transition-all",
        selected
          ? "border-green-500 bg-green-900/20 shadow-md shadow-green-500/20"
          : disabled || isMaxedOut
          ? "border-border-default bg-surface-2/50 opacity-50 cursor-not-allowed"
          : "border-border-default hover:border-primary/50 cursor-pointer",
        doubleSlot && "border-4",
        emphasized && "bg-yellow-500/5"
      )}
      onClick={canClick ? onSelect : undefined}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Checkboxes - Show tier-wide usage */}
          <div className="flex gap-1 flex-shrink-0 pt-0.5">
            {hasThreeBoxes && (
              <>
                {usedSlots >= 1 ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-gray-500" />}
                {usedSlots >= 2 ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-gray-500" />}
                {usedSlots >= 3 ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-gray-500" />}
              </>
            )}
            {hasTwoBoxes && (
              <>
                {usedSlots >= 1 ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-gray-500" />}
                {usedSlots >= 2 ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-gray-500" />}
              </>
            )}
            {!hasThreeBoxes && !hasTwoBoxes && !doubleSlot && (
              usedSlots >= 1 ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-gray-500" />
            )}
            {doubleSlot && (
              <>
                {selected ? <CheckSquare className="w-6 h-6 text-primary" /> : <Square className="w-6 h-6 text-gray-500" />}
                {selected ? <CheckSquare className="w-6 h-6 text-primary" /> : <Square className="w-6 h-6 text-gray-500" />}
              </>
            )}
          </div>

          {/* Content */}
          <div className="flex-1">
            <p className={cn(
              "text-small",
              selected ? "text-gray-100 font-medium" : "text-gray-300",
              emphasized && "italic"
            )}>
              {title}
            </p>

            {doubleSlot && (
              <div className="mt-2 text-tiny text-yellow-400 font-medium">
                ⚠ Costs 2 advancement slots
              </div>
            )}

            {/* Tier-wide usage indicator */}
            {(hasThreeBoxes || hasTwoBoxes) && (
              <div className={cn(
                "mt-2 text-tiny font-medium",
                isMaxedOut ? "text-red-400" : "text-gray-500"
              )}>
                {isMaxedOut
                  ? `⚠ All slots used in Tier ${tier} (${usedSlots}/${maxSlots})`
                  : `Tier ${tier} usage: ${usedSlots}/${maxSlots}`
                }
              </div>
            )}

            {children && (
              <div className="mt-3">
                {children}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * TraitSelector - Select which traits to upgrade
 */
function TraitSelector({ character, markedTraits, selectedTraits, onTraitToggle, tier }) {
  const traits = ['Agility', 'Strength', 'Finesse', 'Instinct', 'Presence', 'Knowledge'];

  return (
    <div>
      <p className="text-small text-gray-400 mb-3">
        Select 2 unmarked traits to upgrade: {selectedTraits.length}/2
      </p>
      <div className="grid grid-cols-2 gap-2">
        {traits.map(trait => {
          const isMarked = markedTraits.includes(trait);
          const isSelected = selectedTraits.includes(trait) && !isMarked; // Can't be selected if marked

          return (
            <button
              key={trait}
              onClick={(e) => {
                e.stopPropagation();
                if (!isMarked) {
                  onTraitToggle(trait);
                }
              }}
              disabled={isMarked}
              className={cn(
                "px-3 py-2 rounded text-small font-medium transition-all duration-200 border text-left relative",
                isMarked
                  ? "bg-gray-900 text-gray-400 border-gray-700 cursor-not-allowed"
                  : isSelected
                  ? "bg-red-900/30 text-red-400 border-red-700"
                  : "bg-gray-800 text-gray-100 border-gray-600 hover:text-red-400 hover:bg-red-900/30 hover:border-red-700"
              )}
            >
              <span className="flex items-center justify-between">
                <span>{trait}</span>
                {isMarked && (
                  <CheckSquare className="w-4 h-4 text-green-500 ml-2" />
                )}
              </span>
            </button>
          );
        })}
      </div>
      {markedTraits.length > 0 && (
        <p className="text-tiny text-gray-500 mt-2 flex items-center gap-1">
          <CheckSquare className="w-3 h-3" />
          Marked traits have already been upgraded this tier
        </p>
      )}
    </div>
  );
}

export default LevelAdvancementEditor;
